// Chat mode (`/c/{slug}/chat`, M2-06; spec 06; CHAT-UX): `/chat` opens a channel (the last one
// visited, else #general) rather than a page of its own, then Browse channels, Saved messages,
// a channel (`/chat/{channelId}`) and a topic in it (`/chat/{channelId}/{topicId}`). The mode
// itself is base product (managers' tab), and every page also needs the `chat` add-on: off
// renders the "not enabled" page, grace renders read-only (the server answers writes
// `feature_read_only`).
import { Alert, AlertDescription, AlertTitle } from "@companyos/ui";
import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ChannelScreen } from "../features/chat/channel-screen";
import {
  ArchivedChannelsPage,
  BrowseChannels,
  ChatLanding,
  SavedMessages,
  ThreadsPage,
  UnreadsPage,
} from "../features/chat/chat-pages";
import { ChatSearchPage, chatSearchParams, MessageLinkPage } from "../features/chat/chat-search";
import { PeoplePage } from "../features/chat/people-page";
import { useCompany, useFeature } from "../features/use-feature";
import { validateFocusSearch } from "../focus/focus";
import { NotEnabledPage } from "../pages/status-pages";
import { ModeGate } from "../shell/mode-gate";
import { companyRoute } from "./c.$slug";

/**
 * The mode's gate plus the add-on's. `full` is a channel: the shell's main area is edge to edge
 * (shell/app-shell.tsx), so the gate's root stretches to its height and the screen fills the rest.
 */
function ChatGate({ children, full = false }: { children: ReactNode; full?: boolean }) {
  const company = useCompany();
  const chat = useFeature("chat");
  const body = !chat.enabled ? (
    <div className={full ? "px-8 py-7 max-md:px-4" : undefined}>
      <NotEnabledPage what="Chat" feature="chat" companyName={company.name} slug={company.slug} />
    </div>
  ) : (
    <>
      {chat.readOnly ? (
        <Alert
          className={`${full ? "m-3 w-auto shrink-0" : "mb-4"} border-nav-read-only bg-nav-read-only text-nav-read-only-foreground`}
        >
          <AlertTitle>Chat is read-only</AlertTitle>
          <AlertDescription className="text-nav-read-only-foreground">
            The Chat add-on is in its grace period: you can read and search, not post.
          </AlertDescription>
        </Alert>
      ) : null}
      {full ? <div className="min-h-0 flex-1">{children}</div> : children}
    </>
  );
  if (!full) return <ModeGate modeKey="chat">{body}</ModeGate>;
  return (
    <div className="h-full [&>[data-mode-root]]:flex [&>[data-mode-root]]:h-full [&>[data-mode-root]]:flex-col">
      <ModeGate modeKey="chat">{body}</ModeGate>
    </div>
  );
}

export const chatRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "chat",
});

export const chatIndexRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "/",
  component: () => (
    <ChatGate full>
      <ChatLanding />
    </ChatGate>
  ),
});

export const chatBrowseRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "browse",
  component: () => (
    <ChatGate full>
      <BrowseChannels />
    </ChatGate>
  ),
});

export const chatUnreadsRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "unreads",
  component: () => (
    <ChatGate full>
      <UnreadsPage />
    </ChatGate>
  ),
});

// CHAT-DM: Slack's People — everyone in the company, with Message and View profile.
export const chatPeopleRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "people",
  component: () => (
    <ChatGate full>
      <PeoplePage />
    </ChatGate>
  ),
});

export const chatSavedRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "saved",
  component: () => (
    <ChatGate full>
      <SavedMessages />
    </ChatGate>
  ),
});

// CHAT-CONTROLS: Slack's Threads view and the archived channels (with Restore).
export const chatThreadsRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "threads",
  component: () => (
    <ChatGate full>
      <ThreadsPage />
    </ChatGate>
  ),
});

export const chatArchivedRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "archived",
  component: () => (
    <ChatGate full>
      <ArchivedChannelsPage />
    </ChatGate>
  ),
});

// CHAT-SWITCH-A: Slack's search page; the words and filters live in the URL (a search is a link).
export const chatSearchRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "search",
  validateSearch: (s: Record<string, unknown>) => ({
    ...validateFocusSearch(s),
    ...chatSearchParams(s),
  }),
  component: () => (
    <ChatGate full>
      <ChatSearchPage />
    </ChatGate>
  ),
});

/** CHAT-SWITCH-B: a conversation's URL keeps `m`, the message a link points at (highlighted). */
const conversationSearch = (s: Record<string, unknown>) => ({
  ...validateFocusSearch(s),
  ...(typeof s.m === "string" && /^[\w-]{1,64}$/.test(s.m) ? { m: s.m } : {}),
});

// CHAT-SWITCH-B: a message link that names the message only (Copy link): where it is now is
// asked of the API, then the conversation opens on it.
export const chatMessageRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "m/$messageId",
  component: function MessageLink() {
    const { messageId } = chatMessageRoute.useParams();
    return (
      <ChatGate full>
        <MessageLinkPage messageId={messageId} />
      </ChatGate>
    );
  },
});

export const chatChannelRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "$channelId",
  validateSearch: conversationSearch,
  component: function Channel() {
    const { channelId } = chatChannelRoute.useParams();
    return (
      <ChatGate full>
        <ChannelScreen key={channelId} channelId={channelId} />
      </ChatGate>
    );
  },
});

export const chatTopicRoute = createRoute({
  getParentRoute: () => chatRoute,
  path: "$channelId/$topicId",
  validateSearch: conversationSearch,
  component: function Topic() {
    const { channelId, topicId } = chatTopicRoute.useParams();
    return (
      <ChatGate full>
        <ChannelScreen key={channelId} channelId={channelId} topicId={topicId} />
      </ChatGate>
    );
  },
});
